import { useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Home, Calendar, MessageSquare, Wallet, User } from 'lucide-react';
import UserAvatar from './UserAvatar';

const API = `${window.location.origin}/api`;

export default function CustomerBottomNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token } = useAuth();
  const [unread, setUnread] = useState(0);

  const refresh = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/support-chat/my/unread-count`, {
        headers: { Authorization: `Bearer ${token || localStorage.getItem('token')}` },
      });
      setUnread(res.data?.unread || 0);
    } catch (e) { /* ignore */ }
  }, [token]);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 15000);
    const onRead = () => refresh();
    window.addEventListener('customer-support-read', onRead);
    return () => { clearInterval(t); window.removeEventListener('customer-support-read', onRead); };
  }, [refresh]);

  const isActive = (path) => {
    if (path === '/home') return location.pathname === '/home' || location.pathname === '/customer';
    return location.pathname.startsWith(path);
  };

  const navItems = [
    { path: '/home', icon: Home, label: 'Home', testId: 'nav-home' },
    { path: '/bookings', icon: Calendar, label: 'Bookings', testId: 'nav-bookings' },
    { path: '/messages', icon: MessageSquare, label: 'Messages', testId: 'nav-messages', badge: unread },
    { path: '/wallet', icon: Wallet, label: 'Wallet', testId: 'nav-wallet' },
    { path: '/profile', label: 'Profile', testId: 'nav-profile', isProfile: true },
  ];

  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-stone-200 z-50" data-testid="customer-bottom-nav">
      <div className="flex items-center justify-around h-16 max-w-lg mx-auto" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
        {navItems.map((item) => {
          const active = isActive(item.path);
          return (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`relative flex flex-col items-center justify-center gap-1 px-3 py-2 rounded-lg transition-colors ${
                active ? 'text-amber-600' : 'text-slate-400 hover:text-slate-600'
              }`}
              data-testid={item.testId}
            >
              {item.isProfile ? (
                user?.profile_image ? (
                  <UserAvatar src={user.profile_image} name={user?.name} role="customer" size="xs" />
                ) : (
                  <User className={`w-6 h-6 ${active ? 'stroke-[2.5]' : ''}`} />
                )
              ) : (
                <item.icon className={`w-6 h-6 ${active ? 'stroke-[2.5]' : ''}`} />
              )}
              {item.badge > 0 && (
                <span
                  className="absolute top-1 right-1.5 min-w-[16px] h-[16px] px-1 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold"
                  data-testid="customer-messages-unread-badge"
                >
                  {item.badge > 9 ? '9+' : item.badge}
                </span>
              )}
              <span className={`text-xs ${active ? 'font-medium' : ''}`}>{item.label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
